import {
  TIER_LEVELS as TIER_LEVELS_CONST,
  ORG_PERMISSIONS,
  type TierLevel as TierLevelType,
} from 'consts/plans';
import { MemberRole } from 'pages/Organization/types';
import { SnakeToCamel } from 'utils/caseTransformers';
import { SnakedGetParams } from '../api.schema';

/*************************
 *    API Consts
 ************************/
export const PERMISSIONS = ORG_PERMISSIONS;

export const TIER_LEVELS = TIER_LEVELS_CONST;

/*************************
 *    Snaked case API
 ************************/
type UserRoles = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export type TierLevel = TierLevelType;

export interface SnakedUserTeam {
  team_id: string;
  permission: UserRoles;
}

export interface SnakedUser {
  allowed: boolean;
  name: string;
  avatar: string | null;
  baseUrl: string | null;
  bitbucket_user_id: string | null;
  bitbucket_user_name: string | null;
  customer_id: string | null;
  email: string;
  github_user_id: string | null;
  github_user_name: string | null;
  gitlab_user_id: string | null;
  gitlab_user_name: string | null;
  id: string;
  last_login: string | null;
  optout: boolean;
  organization_id?: string;
  organization_permission: UserRoles;
  permission: UserRoles;
  stripe_subscription_id: string | null;
  teams: SnakedUserTeam[];
  tier_end_date: string | null;
  tier_level: TierLevel;
  tier_ref: string | null;
  used_trial: boolean;
  user_id: string | null;
  verified: boolean;
  codiumate?: boolean;
  aware_marketing_user?: boolean;
  aware_marketing_user_created_at?: string;
  support_rag_pages?: boolean;
  enterprise_user_name: string;
  billing_dashboard_enabled?: boolean | null;
  is_new_workspace?: boolean;
  workspace_id?: string | null;
  workspace_created_at?: string | null;
  account_id?: string | null;
  is_delegated_team_admin?: boolean;
  can_edit_admin_pages?: boolean;
}

type SnakedGetUserResponse = {
  user: SnakedUser;
};

export type IndexingStatusName = 'Done' | 'Failed' | 'Pending';

type SnakedIndexingStatus = {
  name: IndexingStatusName;
  last_update_utc: string;
};

type SnakedIndexingStatisticsItem = {
  namespace: string;
  name: string;
  status: SnakedIndexingStatus;
  last_success_utc: string;
  number_of_files_indexed?: number;
  number_of_chunks?: number;
};

type SnakedIndexingConfigurationItem = {
  repo_size_limit_gb: number;
  indexing_interval_hours: number;
  last_index_utc: string;
  next_index_utc: string;
};

type SinglePlan = {
  id: string;
  name: string;
  description: string;
  features: string[];
  prices: {
    id: string;
    currency: string;
    interval: string;
    price: number;
  }[];
};

type SnakedTeamPlans = {
  active_plan_name: string;
  is_trialing: boolean;
  plans: SinglePlan[];
};

type SnakedOrganizationAccessRequest = {
  requests: {
    id: string;
    email: string;
    date_created?: string;
    enterprise_user_name: string;
  }[];
};

type SnakedGetIndexingStatisticsResponse = SnakedIndexingStatisticsItem[];

type BaseSessionPayload = {
  price_id?: string;
  redirect_url: string;
};
export type SessionResponse =
  | {
      portal_url: string;
    }
  | undefined;

export type SSOSetupResponse =
  | {
      portal_url: {
        link: string;
      };
    }
  | undefined;

export type SnakedOrganization = {
  id: string;
  name: string;
  sso_organization_id: string | null;
  usage_dashboard_url: string;
  type: string;
  tier_level: number;
  customer_id: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  baseUrl: string | null;
  baseUrlOptions: string | null;
  domains: string[];
  github_ids: string[];
  bitbucket_ids: string[];
  gitlab_ids: string[];
  invite_only: boolean;
  support_rag_pages: boolean;
  git_linking_enabled: boolean;
  allow_team_admins: boolean;
  edit_permission_level: EditPermissionLevel;
  billing_admin: string | null;
  allowed_users: number;
  account_id: string | null;
  workspace_id: string | null;
};

/**
 * Lowest org role granted edit access to the admin-gated pages (Repositories
 * incl. git-app install, Review Standards, Configuration). `org_admin` is the
 * restrictive default; `member` lets any org member edit.
 */
export type EditPermissionLevel = 'org_admin' | 'team_owner' | 'member';

export type SnakedGetOrganizationMembersResponse = {
  page_size: number;
  offset: number;
  total_count: number;
  data: SnakedOrganizationMember[];
};

export type Team = {
  id: string;
  name: string;
};

export type InviteStatusCode =
  | 'INVITE_FILE_EMPTY'
  | 'INVITE_INVALID_FILE_FORMAT'
  | 'INVITE_FILE_TOO_LARGE'
  | 'INVITE_ERROR_PROCESSING_FILE'
  | 'INVITE_MISSING_EMAILS_OR_FILE'
  | 'INVITE_INVALID_EMAIL_ENTRIES'
  | 'INVITE_TOO_MANY_EMAILS'
  | 'INVITE_INVALID_ENTRIES_IN_FILE';

//TODO: https://linear.app/qodo/issue/PLT-1996/import-csv-no-hint-how-to-fix-faulty-csv-rows
//remove InviteErrorType when InviteStatusCode is used everywhere
export type InviteErrorType =
  | 'EMAIL_SYNTAX_ERROR'
  | 'USER_NAME_SYNTAX_ERROR'
  | 'UNKNOWN_TYPE';

export type InviteError = {
  rowNumber: number;
  field: string;
  internalStatusCode?: InviteStatusCode;
  errorType: string;
  value: string;
};

export type InviteOrganizationError = {
  error: {
    message: string;
    detailedErrors: InviteError[];
  };
};

export type SnakedTeamData = {
  id: string;
  name: string;
  active: boolean;
  date_created: string;
  tier_level: TierLevel;
  owner_user_id: string | string[];
  users: string[];
  customer_id?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  organization_id?: string;
  baseUrl?: string;
  workspace_id?: string | null;
};

export type SnakedIsFeatureEnabledResponse = boolean;

/*************************
 *     Camel case API
 *************************/
export type User = SnakeToCamel<SnakedUser>;

export type UserTeam = SnakeToCamel<SnakedUserTeam>;

export type IndexingStatisticsItem = SnakeToCamel<SnakedIndexingStatisticsItem>;

export type IndexingConfigurationItem =
  SnakeToCamel<SnakedIndexingConfigurationItem>;

export type Organization = SnakeToCamel<SnakedOrganization>;

export type TeamData = SnakeToCamel<SnakedTeamData>;

export type IsFeatureEnabledResponse =
  SnakeToCamel<SnakedIsFeatureEnabledResponse>;

/*************************
 *         GET
 *************************/
export type GetUserResponse = SnakeToCamel<SnakedGetUserResponse>;

export type GetIndexingStatisticsResponse =
  SnakeToCamel<SnakedGetIndexingStatisticsResponse>;

export type GetIndexingConfigurationResponse =
  SnakeToCamel<SnakedIndexingConfigurationItem>;

export type GetPlansResponse = SnakeToCamel<SnakedTeamPlans>;

export type GetOrganizationAccessRequestsResponse =
  SnakeToCamel<SnakedOrganizationAccessRequest>;

export type OrganizationAccessRequest =
  GetOrganizationAccessRequestsResponse['requests'][number];

export type GetOrganizationResponse = {
  organization: Organization;
};

export type GetOrganizationMembersResponse =
  SnakeToCamel<SnakedGetOrganizationMembersResponse>;

export type SnakedOrganizationMember = {
  name: string | null;
  email: string;
  last_login: string | null;
  type: string;
  linked_git_accounts: string[] | null;
  team: Team | null;
  team_role: MemberRole | null;
  is_organization_owner: boolean;
  baseUrl: string | null;
  enterprise_user_name: string | null;
  member_id: string; // Document ID for invites, user_id (email) for users
};

export type GetOrganizationMembersParams = SnakeToCamel<SnakedGetParams>;

export type OrganizationMember = SnakeToCamel<SnakedOrganizationMember>;

// Organization members by environment
export type GetOrganizationEnvMembersResponse = {
  [baseUrl: string]: string[]; // Maps baseUrl to array of user IDs
};

// Tier 2.0 (PAID) endpoint types for /teams/{id}/users
export type SnakedTeamUser = {
  id: string;
  name: string | null;
  email: string;
  last_login: string | null;
  avatar: string | null;
  date_created: string;
  verified: boolean;
  permission: UserRoles;
  github_user_id: string | null;
  github_user_name: string | null;
  gitlab_user_id: string | null;
  gitlab_user_name: string | null;
  bitbucket_user_id: string | null;
  bitbucket_user_name: string | null;
  baseUrl: string | null;
  teams: SnakedUserTeam[];
};

export type SnakedTeamInvite = {
  id: string;
  name: string | null;
  email: string;
  date_sent: string;
  permission: UserRoles;
};

export type SnakedGetTeamUsersResponse = {
  users: SnakedTeamUser[];
  invites: SnakedTeamInvite[];
};

export type TeamUser = SnakeToCamel<SnakedTeamUser>;
export type TeamInvite = SnakeToCamel<SnakedTeamInvite>;
export type GetTeamUsersResponse = SnakeToCamel<SnakedGetTeamUsersResponse>;

export type SnakedGetTeamsResponse = {
  teams: SnakedTeamData[];
};

export type GetTeamsResponse = SnakeToCamel<SnakedGetTeamsResponse>;

/*************************
 *         POST
 *************************/
export type CreateCustomerPortalSessionPayload = BaseSessionPayload;

export type CreateCheckoutSessionPayload = BaseSessionPayload;

export type CreateOrganizationTeamPayload = {
  team_name: string;
  team_owner?: string;
};

export type CreateOrganizationTeamResponse = {
  team: TeamData;
  user: SnakedUser;
};

export type SSOSetupPayload = {
  redirect_url: string;
};

/*************************
 *         PATCH
 *************************/
export type UserAttributes = Partial<{
  name: string;
  optout: boolean;
  permission: UserRoles;
  organization_permission: UserRoles;
  baseUrl: string;
  aware_marketing_user?: boolean;
  aware_marketing_user_created_at?: string;
  enterprise_user_name: string;
  team_id: string | null;
}>;

export type UpdateUserPayload = {
  attributes: UserAttributes;
  user_id: string;
};

export type UpdateUserResponse = SnakedGetUserResponse | undefined;

export type UpdateOrganizationTeamPayload = {
  team_name: string;
};

export type UpdateOrganizationTeamResponse = {
  team: TeamData;
  user: SnakedUser;
};
